/* tslint:disable:indent */
/*
 * Copyright (c) 2014-2024 Comparatio USA  LLC. <https://www.comparatio.com>
 */

import {Component, ElementRef, OnDestroy, OnInit, ViewChild, Inject} from '@angular/core';
import {Page} from '../../../model/page';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/delay';
import {ToastrService} from 'ngx-toastr';
import {ActivatedRoute, ActivatedRouteSnapshot, Router} from '@angular/router';
import {permissions} from '../../../permissions';
import { TableExtensionService } from '../../../services/table-extension.service';
import { SortService } from '../../../services/sort.service';
import { AuthService } from '../../../auth/auth.service';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs/Subscription';
import { DatesService } from 'app/services/dates.service';
import { IInvoiceRunTime } from './interfaces/IInvoiceRunTime';
import tableCols from './invoice-run-time.cols';
import { InvoiceRunTimeService } from '../../../services/invoice-run-time.service';
import {Chart, Highcharts} from 'angular-highcharts';
import {HttpClientModule} from '@angular/common/http';
import {IInvoiceDurationDates} from './interfaces/IInvoiceDurationDates';
import * as moment from 'moment';
import {MatDialog, MatDialogRef, MAT_DIALOG_DATA} from '@angular/material';
import {saveAs} from '@progress/kendo-file-saver';
import * as Excel from 'exceljs';
import { CompanyService } from 'app/services/company.service';
import {IInvoiceCompany} from './interfaces/IInvoiceCompany';

@Component({
    providers: [
		InvoiceRunTimeService,
        AuthService,
        TableExtensionService,
        SortService,
        DatesService,
        HttpClientModule
    ],
    selector: 'app-invoice-run-time',
    templateUrl: './invoice-run-time.component.html',
    styleUrls: ['./invoice-run-time.component.scss']
})
export class InvoiceRunTimeComponent implements OnInit, OnDestroy {
    @ViewChild ( 'dataView' ) dataView: ElementRef;
    @ViewChild ( 'actionbar' ) actionbar: ElementRef;
    @ViewChild ( 'content' ) content: ElementRef;
    @ViewChild ( 'tableHeader' ) tableHeader: ElementRef;
    @ViewChild ( 'paginator' ) paginator: ElementRef;
    public loading = false;
    public page = new Page();
    public cols = [];
    public rows = [];
	public size = 50;
    public selected: any = null;
    public totalRecords = 0;
    public scrollHeight = '50px';
    private subscriptions: Array<Subscription>;
    public errors: Array<any> = [];
    public action = '';
    public permissions = permissions;
    public hideTooltip = false;
    private viewportHeightListener: boolean;
    private viewportHeight = 0;
    public record: IInvoiceRunTime;
	public dates: IInvoiceDurationDates;
    public companyModel: IInvoiceCompany;
	public filtersApplied = false;
	public sortApplied: boolean;
	public sortField: string;
	public sortOrder: number;
	public viewFilters = false;
	public resultsFiltered = false;
	public resultsFilteredByDate = false;
	public downloadType = '';
	public downloadTypes = ['.CSV', '.XLSX'];
	public reportData: any;
	public reportRange: any;
	public xaxis = [];
	public yaxis1 = [];
	public yaxis2 = [];
	public chart;
	private initDate1: string;
	private initDate2: string;
    public companyList: Array<any> = [];

    constructor(
        private invoiceRunTimeService: InvoiceRunTimeService,
        private toastr: ToastrService,
        private router: Router,
        private route: ActivatedRoute,
        private title: Title,
        private tableExtensionService: TableExtensionService,
        private sortService: SortService,
        public dialog: MatDialog,
        private companyService: CompanyService,
    ) {
        this.page.pageNumber = 0;
        this.page.size = 50;
        this.title.setTitle ( 'Invoice Run Time' );
    }

    public ngOnInit() {
        this.loading = true;
	    this.sortApplied = false;
	    this.sortField = '';
	    this.sortOrder = 0;

        this.viewportHeightListener = true;

	    const filterParams = this.route.snapshot.queryParams;
	    this.parseParams(filterParams);

        this.subscriptions = [];
        this.cols = tableCols;

        this.record = this.invoiceRunTimeService.initialize ();
	    this.dates = this.invoiceRunTimeService.initializeDates();
        this.companyModel = this.invoiceRunTimeService.initializeCompany();

        this.companyService.getShortCompanyList().subscribe(response => {
            this.companyList = response.companyData;
        });

	    Highcharts.setOptions({
		    lang: {
			    thousandsSep: ','
		    }
	    });

	    const date1 = new Date();
	    date1.setUTCFullYear(date1.getUTCFullYear());
	    date1.setUTCMonth(date1.getUTCMonth());
	    date1.setUTCDate(date1.getUTCDate() - 1);
	    date1.setUTCHours(date1.getUTCHours());
	    date1.setUTCMinutes(date1.getUTCMinutes());
	    date1.setUTCSeconds(date1.getUTCSeconds());
	    date1.setUTCMilliseconds(date1.getUTCMilliseconds());
	    const date2 = new Date();
	    date2.setUTCFullYear(date1.getUTCFullYear());
	    date2.setUTCMonth(date1.getUTCMonth() - 3);
	    date2.setUTCDate(date1.getUTCDate());
	    date2.setUTCHours(date1.getUTCHours());
	    date2.setUTCMinutes(date1.getUTCMinutes());
	    date2.setUTCSeconds(date1.getUTCSeconds());
	    date2.setUTCMilliseconds(date1.getUTCMilliseconds());

	    this.initDate1 = date1.toISOString();
	    this.initDate2 = date2.toISOString();

	    this.resultsFilteredByDate = true;

	    this.filterResults();
        this.screenHeightListener();
    }

    public ngOnDestroy (): void {
        this.viewportHeightListener = false;
        for ( const element of this.subscriptions ) {
            if (element) {
                element.unsubscribe();
            }
        }
    }

	parseParams(filterParams) {
		if (filterParams !== undefined && filterParams !== null) {
			let showFilters = false;
			let sortApplied = false;
			for (const key of Object.keys(filterParams)) {
				const value = filterParams[key];
				if (value && value.toString() !== '') {
					if (key.toString() === 'page') {
						this.page.pageNumber = Number(value);
					} else if (key.toString() === 'perPage') {
						this.page.size = Number(value);
						this.size = Number(value);
					} else if (
						key.toString() === 'SORT_COLUMN' ||
						key.toString() === 'SORT_DIRECTION'
					) {
						this.page.filterParams[key] = value;
						sortApplied = true;
					} else {
						this.page.filterParams[key] = value;
						showFilters = true;
					}
				}
			}
			this.viewFilters = showFilters;
			this.filtersApplied = showFilters;
			this.sortApplied = sortApplied;

			this.parseSort(filterParams);
		}
	}

	private parseSort(filterParams) {
		const sortDetails = this.sortService.parseSort(filterParams);
		this.sortField = sortDetails.sortField;
		this.sortOrder = sortDetails.sortOrder;
	}

    public getData (reloadWithParams = true) {
        this.setPage({ offset: this.page.pageNumber }, reloadWithParams);
    }

    public setPage (pageInfo, reloadWithParams = true) {
        if (reloadWithParams) {
            this.page.filterParams['CO1_ID'] = this.companyModel;
            this.page.filterParams['page'] = pageInfo.offset ? pageInfo.offset : 0;
            this.page.filterParams['perPage'] = pageInfo.size
                ? pageInfo.size
                : this.size;
            this.router.routeReuseStrategy.shouldReuseRoute = function (
                future: ActivatedRouteSnapshot,
                curr: ActivatedRouteSnapshot
            ) {
                return false;
            };
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: this.page.filterParams,
            });
        } else {
            this.loading = true;
            this.page.pageNumber = pageInfo.offset;
            this.subscriptions.push(
                this.invoiceRunTimeService.getDataList(this.page).subscribe(result => {
                    this.rows = result.data;
                    this.totalRecords = result.count;

                    setTimeout(() => {
                        this.scrollHeight = this.getScrollHeight() + 'px';
                    });

                    this.loading = false;
                }, () => {
                    this.loading = false;
                    window.dispatchEvent(new Event('resize'));
                    this.toastr.error('Loading error occurred', 'Service Error', {
                        timeOut: 3000,
                    });
                })
            );
        }
    }

    public onHover(e: { target: { offsetWidth: number; scrollWidth: number; }; }, index = null) {
        const aux = e.target.offsetWidth < e.target.scrollWidth;
        this.hideTooltip = !aux;
    }

	buttonPress(e, col, value) {
		if (e.keyCode === 13) {
			this.filterChanged(col, value);
		}
	}

	clearFilters() {
		this.page.filterParams = {};
		this.viewFilters = false;
		this.filtersApplied = false;
		this.page.pageNumber = 0;
		this.setPage({ offset: this.page.pageNumber });
	}

	filterChanged(col, value) {
		this.loading = true;
		this.page.filterParams[col] = value;
		this.filtersApplied = Object.keys(this.page.filterParams).length > 0;
		this.page.pageNumber = 0;
		this.setPage({ offset: this.page.pageNumber });
	}

    public getContentDialogHeight (): number {
        return (
            window.innerHeight - (
                this.actionbar.nativeElement.offsetHeight +
                48
            ) - 77
        );
    }

    public getScrollHeight (): number {
        return this.getContentDialogHeight () - (
            this.tableHeader.nativeElement.offsetHeight
            + this.paginator.nativeElement.offsetHeight + 52 ? 52 : 0
        );
    }

    public onColumnResize ( event: any ) {
        this.tableExtensionService.onColumnResize ( this.cols, event );
    }

    public refreshClick () {
	    this.dates.DATE1 = null;
	    this.dates.DATE2 = null;
	    this.resultsFiltered = false;
        this.getData (false);
    }

	toggleFilterView() {
		this.viewFilters = !this.viewFilters;
		this.onWindowResize();
	}

	onWindowResize() {
		setTimeout(() => {
			this.scrollHeight = this.getScrollHeight() + 'px';
			window.dispatchEvent(new Event('resize'));
		}, 150);
	}

    public screenHeightListener() {
        if (!this.viewportHeightListener) {
            return;
        }
        if (window.innerHeight !== this.viewportHeight) {
            this.viewportHeight = window.innerHeight;
            this.onWindowResize();
        }
        setTimeout(() => {
            this.screenHeightListener();
        }, 500);
    }

    public backClick () {
        this.loading = false;
        this.router.navigate(['/dashboard']);
    }

	onPage(event) {
		this.page.size = event.rows;
		this.setPage({
			offset: event.first / event.rows
		});
	}

	onSort(col) {
		if (col.sort) {
			if (this.page.filterParams.hasOwnProperty('SORT_COLUMN')) {
				if (this.page.filterParams['SORT_COLUMN'] === col.field) {
					if (this.page.filterParams['SORT_DIRECTION'] === 'ASC') {
						this.page.filterParams['SORT_DIRECTION'] = 'DESC';
					} else {
						delete this.page.filterParams['SORT_DIRECTION'];
						delete this.page.filterParams['SORT_COLUMN'];
					}
				} else {
					this.page.filterParams['SORT_COLUMN'] = col.field;
					this.page.filterParams['SORT_DIRECTION'] = 'ASC';
				}
			} else {
				this.page.filterParams['SORT_COLUMN'] = col.field;
				this.page.filterParams['SORT_DIRECTION'] = 'ASC';
			}

			this.parseSort(this.page.filterParams);

            if (this.resultsFilteredByDate) {
                this.filterResults();
            } else {
                this.getData();
            }
		}
	}
    
    public filterResults() {
        this.loading = true;
        
        if (this.companyModel.CO1_ID) {
            this.page.filterParams['CO1_ID'] = this.companyModel.CO1_ID;
        } else {
            this.page.filterParams['CO1_ID'] = this.companyList[0];
        }
        
        if (this.dates.DATE1 == '') {
            this.dates.DATE1 = this.initDate2;
            this.dates.DATE2 = this.initDate1;
        }
        
        this.page.filterParams['DATE1'] = this.dates.DATE1;
        this.page.filterParams['DATE2'] = this.dates.DATE2;
        
        this.resultsFilteredByDate = true;
        
        this.subscriptions.push(
            this.invoiceRunTimeService.filterResultsList(this.page).subscribe(result => {
                this.rows = result.data;
                this.totalRecords = result.count;
                
                setTimeout(() => {
                    this.scrollHeight = this.getScrollHeight() + 'px';
                });
                
                this.loading = false;
            }, () => {
                this.loading = false;
                window.dispatchEvent(new Event('resize'));
                this.toastr.error('Loading error occurred', 'Service Error', {
                    timeOut: 3000,
                });
            })
        );
        
        this.populateChart();
        this.loading = false;
    }
    public populateChart () {
        this.page.filterParams['CO1_ID'] = this.companyModel.CO1_ID;
        this.page.filterParams['DATE1'] = this.dates.DATE1;
        this.page.filterParams['DATE2'] = this.dates.DATE2;
        this.xaxis = [];
        this.yaxis1 = [];
        this.yaxis2 = [];
        this.resultsFilteredByDate = true;
        this.invoiceRunTimeService.runReport(this.page).subscribe(result => {
            if (result.success) {
                this.reportData = result.data;
                this.reportRange = result.dates;

                for (let i = 0; i < this.reportData.length; i++) {
                    const xdata = this.reportData[i]['API_MONTH'].toString() + '/' + this.reportData[i]['API_YEAR'].toString();
                    const ydata1 = parseInt(this.reportData[i]['API_COUNT'], 10);
                    const ydata2 = this.reportData[i]['AVG_API_RUN_TIME'];
                    this.xaxis.push(xdata);
                    this.yaxis1.push(ydata1);
                    this.yaxis2.push(ydata2);
                }
                
                this.chart = new Chart({
                    chart: {
                        backgroundColor: {
                            linearGradient: [0, 0, 500, 500],
                            stops: [
                                [0, '#ffffff'],
                                [1, '#c2cfd6']
                            ]
                        }
                    },
                    title: {
                        text: 'Invoice Run Time',
                        align: 'left',
                        style: {
                            color: '#151b1e',
                            font: 'bold 16px "Roboto", Verdana, sans-serif'
                        }
                    },
                    subtitle: {
                        text: 'Invoices per month in bars, and average Invoice run time as a line.',
                        align: 'left',
                        style: {
                            color: '#3e515b',
                            font: 'bold 12px "Roboto", Verdana, sans-serif'
                        }
                    },
                    credits: {
                        enabled: false
                    },
                    xAxis: [{
                        type: 'categories',
                        categories: this.xaxis,
                        crosshair: true,
                        labels: {
                            autoRotation: [-45, -90],
                            style: {
                                fontSize: '13px',
                                fontFamily: 'Roboto, sans-serif',
                                color: '#151b1e'
                            }
                        },
                        title: {
                            text: 'Month/Year',
                            style: {
                                color: '#151b1e'
                            }
                        }
                    }],
                    yAxis: [{
                        labels: {
                            format: '{value} seconds',
                            style: {
                                color: '#151b1e'
                            }
                        },
                        title: {
                            text: 'Average Invoice Run Time (sec)',
                            style: {
                                color: '#151b1e'
                            }
                        },
                        minorGridLineWidth: 0,
                        gridLineWidth: 0,
                        alternateGridColor: null
                    },
                        {
                            min: 0,
                            title: {
                                text: 'Number of Invoices',
                                style: {
                                    color: '#151b1e'
                                }
                            },
                            labels: {
                                format: '{value} Invoices',
                                style: {
                                    color: '#151b1e'
                                }
                            },
                            opposite: true
                        }],
                    legend: {
                        align: 'left',
                        verticalAlign: 'top',
                        backgroundColor:
                            'rgba(255,255,255,0.25)',
                        itemStyle: {
                            font: '9pt Roboto, Verdana, sans-serif',
                            color: '#151b1e'
                        },
                        itemHoverStyle: {
                            color: '#536c79'
                        }
                    },
                    tooltip: {
                        shared: true,
                    },
                    plotOptions: {
                        area: {
                            marker: {
                                radius: 0
                            },
                            lineWidth: 2,
                            states: {
                                hover: {
                                    lineWidth: 1
                                }
                            },
                            threshold: null
                        }
                    },
                    loading: {
                        hideDuration: 100,
                        labelStyle: {'fontWeight': 'bold', 'position': 'relative', 'top': '45%'},
                        showDuration: 100,
                        style: {'position': 'absolute', 'backgroundColor': '#ffffff', 'opacity': 0.5, 'textAlign': 'center'},
                    },
                    series: [{
                        name: 'Invoices (Count)',
                        type: 'column',
                        yAxis: 1,
                        data: this.yaxis1,
                        color: {
                            linearGradient: [0, 0, 500, 500],
                            stops: [
                                [0, '#292f56'],
                                [0.025, '#32315b'],
                                [0.05, '#3b3460'],
                                [0.075, '#443664'],
                                [0.1, '#4d3868'],
                                [0.125, '#573a6b'],
                                [0.15, '#603c6e'],
                                [0.175, '#6a3e70'],
                                [0.2, '#733f72'],
                                [0.225, '#7d4173'],
                                [0.25, '#864374'],
                                [0.275, '#904574'],
                                [0.3, '#984773'],
                                [0.325, '#a14a73'],
                                [0.35, '#a94c71'],
                                [0.375, '#b15070'],
                                [0.4, '#b9536e'],
                                [0.425, '#c0576b'],
                                [0.45, '#c65c69'],
                                [0.475, '#cc6166'],
                                [0.5, '#d16662'],
                                [0.525, '#d66c5f'],
                                [0.55, '#da735c'],
                                [0.575, '#dd7958'],
                                [0.6, '#e08055'],
                                [0.625, '#e28851'],
                                [0.65, '#e38f4e'],
                                [0.675, '#e4974b'],
                                [0.7, '#e39f48'],
                                [0.725, '#e2a746'],
                                [0.75, '#e0af45'],
                                [0.775, '#deb844'],
                                [0.8, '#dbc045'],
                                [0.825, '#d6c947'],
                                [0.85, '#d2d14a'],
                                [0.875, '#ccd94f'],
                                [0.9, '#c5e255'],
                                [0.925, '#beea5d'],
                                [0.95, '#b5f266'],
                                [1, '#acfa70']
                            ]
                        }
                    }, {
                        name: 'Avg. Invoice Run Time (Seconds)',
                        type: 'spline',
                        data: this.yaxis2,
                        color: '#151b1e'
                    }],
                    lang: {
                        noData: 'No Data to Display',
                        loading: 'Loading ...'
                    },
                    noData: {
                        style: {
                            fontSize: '13px',
                            fontFamily: 'Roboto, sans-serif',
                            color: '#151b1e'
                        }
                    }
                });
            } else {
                this.errors = result.errors;
                this.toastr.error('Request timed out', 'Service Error');
            }
        });
        this.loading = false;
    }

	clearResultFilter() {
		this.dates.DATE1 = null;
		this.dates.DATE2 = null;
		this.resultsFiltered = false;
		this.getData(false);
	}

    onCompanyChange($event) {
        this.filterResults();
    }

	openDialog(): void {
		const dialogRef =
			this.dialog.open(InvoiceRuntimeDownloadDialogComponent, {
				width: '500px',
				data: { downloadType: this.downloadType, dTypes: this.downloadTypes }
			});

		dialogRef.afterClosed().subscribe(result => {
			console.log('The dialog was closed');
			this.downloadFile(result);
		});
	}

	private downloadFile(result) {
		const reportData = this.rows;
		let filename = 'AsnRunTime';
		const formattedDate = (moment()).format('YYYYMMDDHHmmss');
		filename += formattedDate;
		const replacer = (key, value) => value === null ? '' : value; // specify how you want to handle null values here
		const header = Object.keys(reportData[0]);

		if (result === 2) {
			const workbook = new Excel.Workbook();
			const worksheet = workbook.addWorksheet('Sheet 1');

			worksheet.columns = [];
			const functionalColumns = [];
			for (let i = 0; i < this.cols.length; i++) {
				const column = {
					header: this.cols[i]['header'],
					key: this.cols[i]['field'],
					width: 20
				};

				functionalColumns.push(column);
				worksheet.columns = functionalColumns;
			}

			for (let i = 0; i < reportData.length; i++) {
				worksheet.addRow({
					LOG_DATE_TIME: reportData[i]['LOG_DATE_TIME'],
					DURATION: reportData[i]['DURATION'],
					SCOPE_NAME: reportData[i]['SCOPE_NAME'],
					MESSAGE_TYPE: reportData[i]['MESSAGE_TYPE'],
					LOG_MESSAGE: reportData[i]['LOG_MESSAGE'],
					ED1_ID: reportData[i]['ED1_ID'],
					ED1_DOC_NO: reportData[i]['ED1_DOC_NO'],
					EDI_LOG_ID: reportData[i]['EDI_LOG_ID'],
					CUSTOMER_NAME: reportData[i]['CUSTOMER_NAME'],
					ED1_TYPE: reportData[i]['ED1_TYPE']
				});
			}

			workbook.xlsx.writeBuffer().then((data: any) => {
				console.log('buffer');
				const blob = new Blob([data], {
					type:
						'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
				});
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				document.body.appendChild(a);
				a.setAttribute('style', 'display: none');
				a.href = url;
				a.download = filename + '.xlsx';
				a.click();
				window.URL.revokeObjectURL(url);
				a.remove();
			});
		} else if (result === 1) {
			const csv = reportData.map(row => header.map(fieldName => JSON.stringify(row[fieldName], replacer)).join(','));
			csv.unshift(header.join(','));
			const csvArray = csv.join('\r\n');
			const blob = new Blob([csvArray], {type: 'text/csv'});
			saveAs(blob, '' + filename + '.csv');
		}
	}
}

@Component({
	selector: 'app-invoice-run-time-download-dialog',
	templateUrl: 'invoice-run-time-download-modal.html',
	styleUrls: ['./invoice-run-time.component.scss']
})
export class InvoiceRuntimeDownloadDialogComponent {
	constructor(
		public dialogRef: MatDialogRef<InvoiceRuntimeDownloadDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: any) { }

	onNoClick(): void {
		this.dialogRef.close();
	}

}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/transaction-reports/invoice-run-time/invoice-run-time.component.ts